import { useState } from "react";
import axios from "axios";
import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import SiteFooter from "@/components/layout/SiteFooter";
import BrandingGraphic from "@/components/BrandingGraphic";
import { AlertTriangle, BarChart3, Building2, ShieldCheck, FileText, Truck, BadgePercent, MapPin } from "lucide-react";
import { toast } from "sonner";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const PublicShell = ({ title, subtitle, children }) => (
  <div className="min-h-screen bg-background flex flex-col">
    <header className="border-b border-border/60 bg-card/80 backdrop-blur">
      <div className="mx-auto max-w-7xl px-4 lg:px-8 h-16 flex items-center justify-between">
        <Link to="/" className="flex items-center gap-3">
          <img src="/assets/eagle-favco.png" alt="The Eagle logo" className="w-9 h-9 rounded-md object-cover border border-white/20" />
          <span className="font-heading font-bold text-lg tracking-tight">THE EAGLE</span>
        </Link>
        <div className="flex items-center gap-2">
          <Button asChild variant="outline" size="sm">
            <Link to="/about">About</Link>
          </Button>
          <Button asChild variant="outline" size="sm">
            <Link to="/navigation">Navigation</Link>
          </Button>
          <Button asChild variant="outline" size="sm">
            <Link to="/emergency-services">Emergency Services</Link>
          </Button>
          <Button asChild variant="outline" size="sm">
            <Link to="/careers">Careers</Link>
          </Button>
          <Button asChild size="sm">
            <Link to="/login">Sign In</Link>
          </Button>
        </div>
      </div>
    </header>

    <main className="flex-1">
      <section className="mx-auto max-w-7xl px-4 lg:px-8 py-10 lg:py-14 space-y-8">
        <BrandingGraphic compact />
        <div>
          <h1 className="text-3xl lg:text-5xl font-heading font-bold tracking-tight">{title}</h1>
          <p className="text-muted-foreground mt-2 max-w-3xl">{subtitle}</p>
        </div>
        {children}
      </section>
    </main>

    <SiteFooter />
  </div>
);

const ContactFormCard = ({ page, title, description, defaultRoleInterest = "general" }) => {
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [form, setForm] = useState({
    name: "",
    email: "",
    company: "",
    phone: "",
    role_interest: defaultRoleInterest,
    message: "",
  });

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    try {
      await axios.post(`${API}/public/contact`, {
        page,
        name: form.name,
        email: form.email,
        company: form.company || null,
        phone: form.phone || null,
        role_interest: form.role_interest || "general",
        message: form.message,
      });
      toast.success("Message sent. Our team will follow up soon.");
      setForm({
        name: "",
        email: "",
        company: "",
        phone: "",
        role_interest: defaultRoleInterest,
        message: "",
      });
    } catch (error) {
      toast.error(error?.response?.data?.detail || "Could not submit inquiry");
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Card>
      <CardHeader>
        <CardTitle>{title}</CardTitle>
        <CardDescription>{description}</CardDescription>
      </CardHeader>
      <CardContent>
        <form className="space-y-4" onSubmit={handleSubmit}>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label htmlFor={`${page}-name`}>Name</Label>
              <Input id={`${page}-name`} value={form.name} onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))} required />
            </div>
            <div className="space-y-2">
              <Label htmlFor={`${page}-email`}>Email</Label>
              <Input id={`${page}-email`} type="email" value={form.email} onChange={(e) => setForm((f) => ({ ...f, email: e.target.value }))} required />
            </div>
            <div className="space-y-2">
              <Label htmlFor={`${page}-company`}>Company</Label>
              <Input id={`${page}-company`} value={form.company} onChange={(e) => setForm((f) => ({ ...f, company: e.target.value }))} />
            </div>
            <div className="space-y-2">
              <Label htmlFor={`${page}-phone`}>Phone</Label>
              <Input id={`${page}-phone`} value={form.phone} onChange={(e) => setForm((f) => ({ ...f, phone: e.target.value }))} />
            </div>
          </div>
          <div className="space-y-2">
            <Label>Role Interest</Label>
            <Select value={form.role_interest} onValueChange={(value) => setForm((f) => ({ ...f, role_interest: value }))}>
              <SelectTrigger>
                <SelectValue placeholder="Select role" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="general">General</SelectItem>
                <SelectItem value="driver">Driver</SelectItem>
                <SelectItem value="dispatcher">Dispatcher</SelectItem>
                <SelectItem value="distributor">Distributor</SelectItem>
                <SelectItem value="sales_rep">Sales Rep</SelectItem>
                <SelectItem value="emergency">Emergency Responder</SelectItem>
                <SelectItem value="investor">Investor</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div className="space-y-2">
            <Label htmlFor={`${page}-message`}>Message</Label>
            <Textarea
              id={`${page}-message`}
              value={form.message}
              onChange={(e) => setForm((f) => ({ ...f, message: e.target.value }))}
              placeholder="Tell us what you need and we will follow up."
              rows={5}
              required
            />
          </div>
          <Button type="submit" disabled={isSubmitting} className="w-full md:w-auto">
            {isSubmitting ? "Submitting..." : "Submit Inquiry"}
          </Button>
        </form>
      </CardContent>
    </Card>
  );
};

export const AboutPage = () => (
  <PublicShell
    title="About The Eagle"
    subtitle="The Eagle is a logistics operations platform connecting drivers, dispatchers, distributors, and admins in one real-time command center."
  >
    <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
      <Card><CardHeader><CardTitle className="flex items-center gap-2"><BarChart3 className="w-4 h-4 text-primary" />Live Operations</CardTitle></CardHeader><CardContent><CardDescription>Live geolocation, shipment visibility, marketplace listings, chat rooms, and actionable analytics for every role.</CardDescription></CardContent></Card>
      <Card><CardHeader><CardTitle className="flex items-center gap-2"><Building2 className="w-4 h-4 text-primary" />Enterprise Ready</CardTitle></CardHeader><CardContent><CardDescription>Role-based controls, payout workflows, BOL signature tracking, marketplace acceptance logs, and centralized admin operations.</CardDescription></CardContent></Card>
      <Card><CardHeader><CardTitle className="flex items-center gap-2"><ShieldCheck className="w-4 h-4 text-primary" />Operational Security</CardTitle></CardHeader><CardContent><CardDescription>Secure authentication, event history, moderation controls, and auditable user actions designed for regulated logistics teams.</CardDescription></CardContent></Card>
    </div>
    <Card>
      <CardHeader><CardTitle>Why This App Exists</CardTitle></CardHeader>
      <CardContent className="text-sm text-muted-foreground space-y-2">
        <p>Freight operations often run on disconnected tools, manual phone trees, and delayed status updates.</p>
        <p>The Eagle addresses that gap by combining dispatch, driver telemetry, digital acceptance records, shipment lifecycle controls, and communication in one interface.</p>
        <p>This reduces handoff friction, improves response times, and creates cleaner operational visibility for each stakeholder without replacing existing business relationships.</p>
      </CardContent>
    </Card>
    <ContactFormCard
      page="about"
      title="Contact The Eagle Team"
      description="Use this form to discuss operations setup, onboarding, and deployment questions."
      defaultRoleInterest="general"
    />
  </PublicShell>
);

export const InvestorsPage = () => (
  <PublicShell
    title="Investor Overview"
    subtitle="The Eagle targets high-friction logistics workflows with a single professional platform focused on realtime data, reliability, and automation."
  >
    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
      <Card>
        <CardHeader><CardTitle>Business Thesis</CardTitle></CardHeader>
        <CardContent className="text-sm text-muted-foreground space-y-2">
          <p>Logistics teams still operate across disconnected systems that slow dispatch quality and reporting accuracy.</p>
          <p>The Eagle unifies dispatch workflows, driver telemetry, marketplace acceptance records, payment orchestration, and communications in one command layer.</p>
        </CardContent>
      </Card>
      <Card>
        <CardHeader><CardTitle>Growth Focus</CardTitle></CardHeader>
        <CardContent className="text-sm text-muted-foreground space-y-2">
          <p>Product-led onboarding across distributors, dispatcher teams, and independent driver fleets.</p>
          <p>Monetization through operational subscriptions, premium analytics, and transaction infrastructure with disciplined scope.</p>
        </CardContent>
      </Card>
    </div>
    <Card>
      <CardHeader><CardTitle>Investment Posture (No Hype)</CardTitle></CardHeader>
      <CardContent className="text-sm text-muted-foreground space-y-2">
        <p>The Eagle is positioned as an operational software business focused on measurable workflow improvement.</p>
        <p>We avoid guaranteed ROI language and instead track adoption, retention, dispatch cycle time, and operational SLA outcomes as primary health metrics.</p>
        <p>Investment suitability depends on execution quality, market adoption, and compliance readiness, not speculative claims.</p>
      </CardContent>
    </Card>
    <ContactFormCard
      page="investors"
      title="Investor Contact"
      description="Request financial briefings, product roadmap sessions, or strategic partnership discussions."
      defaultRoleInterest="investor"
    />
  </PublicShell>
);

export const CareersPage = () => (
  <PublicShell
    title="Careers: Drivers and Sales Reps"
    subtitle="Join The Eagle network as a professional driver or a commission-based sales rep focused on logistics growth."
  >
    <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2"><Truck className="w-4 h-4 text-primary" />Driver Opportunities</CardTitle>
          <CardDescription>Take loads, track live routes, sign BOLs, and manage your wallet credits from one dashboard.</CardDescription>
        </CardHeader>
        <CardContent className="text-sm text-muted-foreground space-y-2">
          <p>Best fit for CDL professionals who want cleaner load coordination, transparent statuses, and digital shipment records.</p>
          <p>Drivers can use live status controls, marketplace load acceptance, and wallet visibility inside the app.</p>
        </CardContent>
      </Card>
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2"><BadgePercent className="w-4 h-4 text-primary" />Sales Rep Program</CardTitle>
          <CardDescription>Share a unique referral link and earn lifetime 5% commission credits on qualifying loads from referred users.</CardDescription>
        </CardHeader>
        <CardContent className="text-sm text-muted-foreground space-y-2">
          <p>Sales reps receive a dedicated dashboard, referral analytics, and wallet credit tracking for commission activity.</p>
          <p>This role is ideal for logistics business developers, broker networkers, and industry relationship builders.</p>
        </CardContent>
      </Card>
    </div>

    <ContactFormCard
      page="careers"
      title="Apply for Driver or Sales Rep Role"
      description="Tell us your background and preferred role."
      defaultRoleInterest="driver"
    />
  </PublicShell>
);

export const TermsPage = () => (
  <PublicShell
    title="Terms of Service"
    subtitle="These terms govern access to and use of The Eagle platform and services."
  >
    <Card>
      <CardHeader><CardTitle className="flex items-center gap-2"><FileText className="w-4 h-4 text-primary" />Terms Summary</CardTitle></CardHeader>
      <CardContent className="text-sm text-muted-foreground space-y-2">
        <p>Use of this platform is limited to lawful logistics and transportation operations.</p>
        <p>Users are responsible for account security, data accuracy, and lawful use of shipping, payment, and tracking functionality.</p>
        <p>Marketplace acceptance signatures are treated as operational acknowledgements and audit records.</p>
        <p>The Eagle may suspend or terminate accounts involved in abuse, fraud, policy violations, or illegal conduct.</p>
      </CardContent>
    </Card>
    <Card>
      <CardHeader><CardTitle>Professional Legal Breakdown</CardTitle></CardHeader>
      <CardContent className="text-sm text-muted-foreground space-y-2">
        <p>No guarantee of revenue, profit, delivery outcome, or uninterrupted service is provided.</p>
        <p>The platform is offered on a best-effort basis with commercial safeguards, but operational users remain responsible for final dispatch and compliance decisions.</p>
        <p>Nothing in this product experience should be interpreted as legal, tax, insurance, or investment advice.</p>
      </CardContent>
    </Card>
  </PublicShell>
);

export const PrivacyPage = () => (
  <PublicShell
    title="Privacy Policy"
    subtitle="This policy describes what data is collected and how it is used to operate The Eagle."
  >
    <Card>
      <CardHeader><CardTitle className="flex items-center gap-2"><ShieldCheck className="w-4 h-4 text-primary" />Privacy Summary</CardTitle></CardHeader>
      <CardContent className="text-sm text-muted-foreground space-y-2">
        <p>The platform stores account profile data, shipment records, payment metadata, acceptance signatures, chat metadata, and location telemetry where enabled.</p>
        <p>Data is used to operate dispatch workflows, fraud controls, auditing, realtime maps, service improvement, and support.</p>
        <p>Administrative users can access records only according to assigned roles and permissions.</p>
      </CardContent>
    </Card>
    <Card>
      <CardHeader><CardTitle>Data Handling Principles</CardTitle></CardHeader>
      <CardContent className="text-sm text-muted-foreground space-y-2">
        <p>Data minimization: only operationally necessary fields are collected for shipment and workflow execution.</p>
        <p>Purpose limitation: telemetry and event data are used for logistics operations and system reliability, not unrelated advertising use.</p>
        <p>Retention discipline: records are retained for operational, contractual, and compliance purposes and removed according to policy and law.</p>
      </CardContent>
    </Card>
  </PublicShell>
);

export const NavigationPage = () => (
  <PublicShell
    title="GPS Navigation"
    subtitle="Use driver-first GPS with admin tracking. Anyone can use navigation, and logged-in drivers sync live trip telemetry to admin."
  >
    <Card>
      <CardHeader>
        <CardTitle>How Navigation Works</CardTitle>
        <CardDescription>Use the in-app GPS console to run full-screen navigation while keeping dispatch telemetry in sync.</CardDescription>
      </CardHeader>
      <CardContent className="text-sm text-muted-foreground space-y-2">
        <p>1. Sign in as a driver and open the GPS launcher at the bottom of the app.</p>
        <p>2. Search destination, select route, and start trip for live turn guidance and ETA.</p>
        <p>3. Admins automatically receive active trip status, destination, progress, and location updates.</p>
      </CardContent>
    </Card>
    <div className="flex flex-wrap gap-3">
      <Button onClick={() => window.dispatchEvent(new CustomEvent("eagle-open-gps"))}>
        Use Driver-First GPS With Admin Tracking
      </Button>
      <Button asChild variant="outline">
        <Link to="/sitemap">View Full Sitemap</Link>
      </Button>
    </div>
  </PublicShell>
);

export const EmergencyServicesPage = () => (
  <PublicShell
    title="Emergency Services"
    subtitle="Find critical response services quickly, understand safe-route support, and know how emergency incidents are reported inside The Eagle."
  >
    <div className="grid gap-4 lg:grid-cols-3">
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2"><AlertTriangle className="w-4 h-4 text-primary" />Emergency Reporting</CardTitle>
          <CardDescription>Map-based incident reporting with severity, type, notes, and precise coordinates.</CardDescription>
        </CardHeader>
        <CardContent className="text-sm text-muted-foreground space-y-2">
          <p>Drivers and operators can drop a pin directly on the map, classify the event, and preserve a timestamped field report for admin response.</p>
          <p>Severity-aware markers keep urgent hazards visible while route generation accounts for active incidents.</p>
        </CardContent>
      </Card>
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2"><MapPin className="w-4 h-4 text-primary" />Service Coverage</CardTitle>
          <CardDescription>Hospitals, police, fire stations, EMS, shelters, urgent care, and pharmacies are surfaced as routeable services.</CardDescription>
        </CardHeader>
        <CardContent className="text-sm text-muted-foreground space-y-2">
          <p>Nearby services are shown with live status badges, marker popups, and quick actions for nearest or fastest routing.</p>
          <p>Safe-route mode prioritizes paths that avoid high-severity incidents when an alternate route is available.</p>
        </CardContent>
      </Card>
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2"><ShieldCheck className="w-4 h-4 text-primary" />Admin Response</CardTitle>
          <CardDescription>Emergency incidents flow into the admin dashboard for acknowledgement, notes, and route oversight.</CardDescription>
        </CardHeader>
        <CardContent className="text-sm text-muted-foreground space-y-2">
          <p>Admins can review response queues, acknowledge or resolve reports, and audit route risk scoring from one control panel.</p>
          <p>Response notes are stored with each report to preserve an operational record of how the incident was handled.</p>
        </CardContent>
      </Card>
    </div>

    <Card>
      <CardHeader>
        <CardTitle>What Users Can Do</CardTitle>
        <CardDescription>Emergency tooling is part of the navigation experience, not a separate disconnected workflow.</CardDescription>
      </CardHeader>
      <CardContent className="text-sm text-muted-foreground space-y-2">
        <p>1. Open GPS and enable Emergency Tools.</p>
        <p>2. Filter services by type and status, or report an incident from the map.</p>
        <p>3. Generate the nearest or fastest route, optionally using safe-route mode to avoid severe incident clusters.</p>
        <p>4. Admin teams review the response queue and close the loop with acknowledgement or resolution notes.</p>
      </CardContent>
    </Card>

    <div className="flex flex-wrap gap-3">
      <Button onClick={() => window.dispatchEvent(new CustomEvent("eagle-open-gps"))}>
        Open GPS Emergency Tools
      </Button>
      <Button asChild variant="outline">
        <Link to="/login">Sign In For Live Emergency Routing</Link>
      </Button>
    </div>
  </PublicShell>
);

export const SiteMapPage = () => (
  <PublicShell
    title="HTML Sitemap"
    subtitle="Quick navigation for core public and authenticated routes."
  >
    <Card>
      <CardContent className="py-6">
        <ul className="space-y-2 text-sm">
          <li><Link className="text-primary hover:underline" to="/login">/login</Link></li>
          <li><Link className="text-primary hover:underline" to="/about">/about</Link></li>
          <li><Link className="text-primary hover:underline" to="/investors">/investors</Link></li>
          <li><Link className="text-primary hover:underline" to="/careers">/careers</Link></li>
          <li><Link className="text-primary hover:underline" to="/navigation">/navigation</Link></li>
          <li><Link className="text-primary hover:underline" to="/emergency-services">/emergency-services</Link></li>
          <li><Link className="text-primary hover:underline" to="/terms">/terms</Link></li>
          <li><Link className="text-primary hover:underline" to="/privacy">/privacy</Link></li>
          <li><Link className="text-primary hover:underline" to="/sitemap">/sitemap</Link></li>
        </ul>
      </CardContent>
    </Card>
  </PublicShell>
);
